import React from 'react';
import { 
  MapPin, Utensils, Coffee, ShoppingBag, Building2, Trees, Camera,
  Landmark, Hotel, Wine, Umbrella, Mountain, Church, Castle, Theater,
  BookOpen, Palette, Tent, Heart, Anchor, TreePine, Home, Sparkles,
  GraduationCap, Music, Car, Waves, Flower2, Ticket, Activity,
  Gamepad2, Fish, Store, ShoppingCart, Plane, Train, Bus,
  Dumbbell, Pill, School, CreditCard, Mail, Badge, Flame,
  Grape, Beer, Building, Square, Map, Wheat
} from 'lucide-react';

interface LocationTypeInfo {
  icon: React.ReactElement;
  label: string;
  type: string;
}

export const getLocationTypeAndIcon = (location: any, iconSize = 14): LocationTypeInfo => {
  const iconProps = { size: iconSize, className: "inline-block mr-1", style: { verticalAlign: 'middle', marginTop: '-2px' } };
  
  // 1. Check restaurant with cuisine
  if (location.type === 'restaurant' && location.cuisine) {
    return {
      icon: <Utensils {...iconProps} />,
      label: location.cuisine,
      type: 'restaurant'
    };
  }
  
  // 2. Check types array (OSM/Google format)
  if (location.types && Array.isArray(location.types)) {
    const typeMap: Record<string, { icon: React.ReactElement; label: string }> = {
      
       // Museums & Culture
      'museum': { icon: <Landmark {...iconProps} />, label: 'Museum' },
      'art_gallery': { icon: <Palette {...iconProps} />, label: 'Art Gallery' },
      'library': { icon: <BookOpen {...iconProps} />, label: 'Library' },
      
      // Food & Drink
      'restaurant': { icon: <Utensils {...iconProps} />, label: 'Restaurant' },
      'cafe': { icon: <Coffee {...iconProps} />, label: 'Cafe' },
      'bar': { icon: <Wine {...iconProps} />, label: 'Bar' },
      'night_club': { icon: <Music {...iconProps} />, label: 'Nightclub' },
      'bakery': { icon: <Coffee {...iconProps} />, label: 'Bakery' },
      
      // Accommodation
      'lodging': { icon: <Hotel {...iconProps} />, label: 'Hotel' },
      'campground': { icon: <Tent {...iconProps} />, label: 'Campground' },
      'rv_park': { icon: <Car {...iconProps} />, label: 'RV Park' },
      
      // Nature & Parks
      'park': { icon: <Trees {...iconProps} />, label: 'Park' },
      'natural_feature': { icon: <Mountain {...iconProps} />, label: 'Natural Feature' },
      'beach': { icon: <Umbrella {...iconProps} />, label: 'Beach' },
      'mountain': { icon: <Mountain {...iconProps} />, label: 'Mountain' },
      'lake': { icon: <Waves {...iconProps} />, label: 'Lake' },
      'waterfall': { icon: <Waves {...iconProps} />, label: 'Waterfall' },
      'garden': { icon: <Flower2 {...iconProps} />, label: 'Garden' },
      'botanical_garden': { icon: <Trees {...iconProps} />, label: 'Botanical Garden' },
      'national_park': { icon: <TreePine {...iconProps} />, label: 'National Park' },
      'forest': { icon: <Trees {...iconProps} />, label: 'Forest' },
      'cave': { icon: <Mountain {...iconProps} />, label: 'Cave' },
      'island': { icon: <Anchor {...iconProps} />, label: 'Island' },
      
      // Religious
      'church': { icon: <Church {...iconProps} />, label: 'Church' },
      'hindu_temple': { icon: <Building2 {...iconProps} />, label: 'Hindu Temple' },
      'mosque': { icon: <Building2 {...iconProps} />, label: 'Mosque' },
      'synagogue': { icon: <Building2 {...iconProps} />, label: 'Synagogue' },
      'place_of_worship': { icon: <Church {...iconProps} />, label: 'Place of Worship' },
      'temple': { icon: <Building2 {...iconProps} />, label: 'Temple' },
      
      // Historic
      'palace': { icon: <Castle {...iconProps} />, label: 'Palace' },
      'castle': { icon: <Castle {...iconProps} />, label: 'Castle' },
      'fort': { icon: <Castle {...iconProps} />, label: 'Fort' },
      'monument': { icon: <Landmark {...iconProps} />, label: 'Monument' },
      'historic_site': { icon: <Landmark {...iconProps} />, label: 'Historic Site' },
      
      // Entertainment
      'movie_theater': { icon: <Ticket {...iconProps} />, label: 'Cinema' },
      'stadium': { icon: <Activity {...iconProps} />, label: 'Stadium' },
      'amusement_park': { icon: <Gamepad2 {...iconProps} />, label: 'Amusement Park' },
      'zoo': { icon: <Fish {...iconProps} />, label: 'Zoo' },
      'aquarium': { icon: <Fish {...iconProps} />, label: 'Aquarium' },
      'bowling_alley': { icon: <Gamepad2 {...iconProps} />, label: 'Bowling' },
      'casino': { icon: <Sparkles {...iconProps} />, label: 'Casino' },
      'theater': { icon: <Theater {...iconProps} />, label: 'Theater' },
      'concert_hall': { icon: <Music {...iconProps} />, label: 'Concert Hall' },
      
      // Shopping
      'shopping_mall': { icon: <ShoppingBag {...iconProps} />, label: 'Shopping Mall' },
      'store': { icon: <Store {...iconProps} />, label: 'Store' },
      'clothing_store': { icon: <ShoppingBag {...iconProps} />, label: 'Clothing Store' },
      'department_store': { icon: <Store {...iconProps} />, label: 'Department Store' },
      'supermarket': { icon: <ShoppingCart {...iconProps} />, label: 'Supermarket' },
      'jewelry_store': { icon: <Sparkles {...iconProps} />, label: 'Jewelry Store' },
      'book_store': { icon: <BookOpen {...iconProps} />, label: 'Book Store' },
      'market': { icon: <ShoppingBag {...iconProps} />, label: 'Market' },
      
      // Transportation
      'airport': { icon: <Plane {...iconProps} />, label: 'Airport' },
      'train_station': { icon: <Train {...iconProps} />, label: 'Train Station' },
      'subway_station': { icon: <Train {...iconProps} />, label: 'Subway Station' },
      'bus_station': { icon: <Bus {...iconProps} />, label: 'Bus Station' },
      'transit_station': { icon: <Train {...iconProps} />, label: 'Transit Station' },
      
      // Health & Wellness
      'spa': { icon: <Heart {...iconProps} />, label: 'Spa' },
      'gym': { icon: <Dumbbell {...iconProps} />, label: 'Gym' },
      'hospital': { icon: <Heart {...iconProps} />, label: 'Hospital' },
      'pharmacy': { icon: <Pill {...iconProps} />, label: 'Pharmacy' },
      
      // Education
      'university': { icon: <GraduationCap {...iconProps} />, label: 'University' },
      'college': { icon: <GraduationCap {...iconProps} />, label: 'College' },
      'school': { icon: <School {...iconProps} />, label: 'School' },
      
      // Services
      'bank': { icon: <CreditCard {...iconProps} />, label: 'Bank' },
      'atm': { icon: <CreditCard {...iconProps} />, label: 'ATM' },
      'post_office': { icon: <Mail {...iconProps} />, label: 'Post Office' },
      'police': { icon: <Badge {...iconProps} />, label: 'Police' },
      'fire_station': { icon: <Flame {...iconProps} />, label: 'Fire Station' },
      'city_hall': { icon: <Building2 {...iconProps} />, label: 'City Hall' },
      
      // Other
      'tourist_attraction': { icon: <Camera {...iconProps} />, label: 'Attraction' },
      'viewpoint': { icon: <Camera {...iconProps} />, label: 'Viewpoint' },
      'winery': { icon: <Grape {...iconProps} />, label: 'Winery' },
      'brewery': { icon: <Beer {...iconProps} />, label: 'Brewery' },
      
      // Generic
      'point_of_interest': { icon: <MapPin {...iconProps} />, label: 'Point of Interest' },
      'establishment': { icon: <Building {...iconProps} />, label: 'Establishment' },
      'place': { icon: <MapPin {...iconProps} />, label: 'Place' }
    };
    
    for (const type of location.types) {
      if (typeMap[type]) {
        return { ...typeMap[type], type };
      }
    }
  }
  
  // 3. Parse location name (fallback)
  const nameLower = location.name.toLowerCase();
  const namePatterns: Array<{ pattern: RegExp; result: LocationTypeInfo }> = [
  // Religious
  { pattern: /temple|mandir|wat|pagoda/, result: { icon: <Building2 {...iconProps} />, label: 'Temple', type: 'temple' }},
  { pattern: /church|cathedral|basilica|chapel/, result: { icon: <Church {...iconProps} />, label: 'Church', type: 'church' }},
  { pattern: /mosque|masjid/, result: { icon: <Building2 {...iconProps} />, label: 'Mosque', type: 'mosque' }},
  { pattern: /synagogue/, result: { icon: <Building2 {...iconProps} />, label: 'Synagogue', type: 'synagogue' }},
  { pattern: /monastery|abbey/, result: { icon: <Church {...iconProps} />, label: 'Monastery', type: 'place_of_worship' }},
  
  // Historic
  { pattern: /palace/, result: { icon: <Castle {...iconProps} />, label: 'Palace', type: 'palace' }},
  { pattern: /castle/, result: { icon: <Castle {...iconProps} />, label: 'Castle', type: 'castle' }},
  { pattern: /fort|fortress/, result: { icon: <Castle {...iconProps} />, label: 'Fort', type: 'fort' }},
  { pattern: /monument|memorial/, result: { icon: <Landmark {...iconProps} />, label: 'Monument', type: 'monument' }},
  { pattern: /ruins/, result: { icon: <Landmark {...iconProps} />, label: 'Historic Site', type: 'historic_site' }},
  
  // Nature
  { pattern: /mountain|peak|hill/, result: { icon: <Mountain {...iconProps} />, label: 'Mountain', type: 'mountain' }},
  { pattern: /lake/, result: { icon: <Waves {...iconProps} />, label: 'Lake', type: 'lake' }},
  { pattern: /beach|bay|cove/, result: { icon: <Umbrella {...iconProps} />, label: 'Beach', type: 'beach' }},
  { pattern: /waterfall|falls/, result: { icon: <Waves {...iconProps} />, label: 'Waterfall', type: 'waterfall' }},
  { pattern: /garden/, result: { icon: <Flower2 {...iconProps} />, label: 'Garden', type: 'garden' }},
  { pattern: /botanical garden/, result: { icon: <Trees {...iconProps} />, label: 'Botanical Garden', type: 'botanical_garden' }},
  { pattern: /national park/, result: { icon: <TreePine {...iconProps} />, label: 'National Park', type: 'national_park' }},
  { pattern: /forest|woods/, result: { icon: <Trees {...iconProps} />, label: 'Forest', type: 'forest' }},
  { pattern: /cave|cavern/, result: { icon: <Mountain {...iconProps} />, label: 'Cave', type: 'cave' }},
  { pattern: /island/, result: { icon: <Anchor {...iconProps} />, label: 'Island', type: 'island' }},
  { pattern: /park/, result: { icon: <Trees {...iconProps} />, label: 'Park', type: 'park' }},
  
  // Infrastructure
  { pattern: /bridge/, result: { icon: <Building2 {...iconProps} />, label: 'Bridge', type: 'bridge' }},
  { pattern: /tower/, result: { icon: <Building2 {...iconProps} />, label: 'Tower', type: 'tower' }},
  { pattern: /lighthouse/, result: { icon: <Anchor {...iconProps} />, label: 'Lighthouse', type: 'lighthouse' }},
  { pattern: /dam/, result: { icon: <Building2 {...iconProps} />, label: 'Dam', type: 'dam' }},
  
  // Entertainment/Culture
  { pattern: /theater|theatre/, result: { icon: <Theater {...iconProps} />, label: 'Theater', type: 'theater' }},
  { pattern: /opera house/, result: { icon: <Theater {...iconProps} />, label: 'Theater', type: 'theater' }},
  { pattern: /concert hall/, result: { icon: <Music {...iconProps} />, label: 'Concert Hall', type: 'concert_hall' }},
  { pattern: /gallery/, result: { icon: <Palette {...iconProps} />, label: 'Art Gallery', type: 'art_gallery' }},
  { pattern: /museum/, result: { icon: <Landmark {...iconProps} />, label: 'Museum', type: 'museum' }},
  { pattern: /market|bazaar/, result: { icon: <ShoppingBag {...iconProps} />, label: 'Market', type: 'market' }},
  { pattern: /mall/, result: { icon: <ShoppingBag {...iconProps} />, label: 'Shopping Mall', type: 'shopping_mall' }},
  
  // Food & Accommodation
  { pattern: /restaurant/, result: { icon: <Utensils {...iconProps} />, label: 'Restaurant', type: 'restaurant' }},
  { pattern: /cafe|coffee/, result: { icon: <Coffee {...iconProps} />, label: 'Cafe', type: 'cafe' }},
  { pattern: /bar|pub/, result: { icon: <Wine {...iconProps} />, label: 'Bar', type: 'bar' }},
  { pattern: /hotel/, result: { icon: <Hotel {...iconProps} />, label: 'Hotel', type: 'hotel' }},
  { pattern: /resort/, result: { icon: <Hotel {...iconProps} />, label: 'Resort', type: 'resort' }},
  
  // Other
  { pattern: /square|plaza/, result: { icon: <Square {...iconProps} />, label: 'Square', type: 'square' }},
  { pattern: /street|avenue|road/, result: { icon: <Map {...iconProps} />, label: 'Street', type: 'street' }},
  { pattern: /district|quarter|neighborhood/, result: { icon: <Home {...iconProps} />, label: 'Neighborhood', type: 'neighborhood' }},
  { pattern: /viewpoint|lookout|observation/, result: { icon: <Camera {...iconProps} />, label: 'Viewpoint', type: 'viewpoint' }},
  { pattern: /hot spring|onsen/, result: { icon: <Heart {...iconProps} />, label: 'Spa', type: 'spa' }},
  { pattern: /winery|vineyard/, result: { icon: <Grape {...iconProps} />, label: 'Winery', type: 'winery' }},
  { pattern: /brewery/, result: { icon: <Beer {...iconProps} />, label: 'Brewery', type: 'brewery' }},
  { pattern: /distillery/, result: { icon: <Wine {...iconProps} />, label: 'Distillery', type: 'distillery' }},
  { pattern: /farm/, result: { icon: <Wheat {...iconProps} />, label: 'Farm', type: 'farm' }},
  { pattern: /ranch/, result: { icon: <Home {...iconProps} />, label: 'Ranch', type: 'ranch' }},
  { pattern: /university|college/, result: { icon: <GraduationCap {...iconProps} />, label: 'University', type: 'university' }},
  { pattern: /school/, result: { icon: <School {...iconProps} />, label: 'School', type: 'school' }},
  { pattern: /library/, result: { icon: <BookOpen {...iconProps} />, label: 'Library', type: 'library' }},
  { pattern: /stadium/, result: { icon: <Activity {...iconProps} />, label: 'Stadium', type: 'stadium' }},
  { pattern: /zoo/, result: { icon: <Fish {...iconProps} />, label: 'Zoo', type: 'zoo' }},
  { pattern: /aquarium/, result: { icon: <Fish {...iconProps} />, label: 'Aquarium', type: 'aquarium' }},
  { pattern: /airport/, result: { icon: <Plane {...iconProps} />, label: 'Airport', type: 'airport' }},
  { pattern: /station/, result: { icon: <Train {...iconProps} />, label: 'Station', type: 'train_station' }}
];
  
  for (const { pattern, result } of namePatterns) {
    if (pattern.test(nameLower)) {
      return result;
    }
  }
  
  // 4. Check type field
  if (location.type) {
    const type = location.type.toLowerCase();
    if (type.includes('attraction')) {
      return { icon: <Camera {...iconProps} />, label: 'Attraction', type: 'tourist_attraction' };
    }
  }
  
  // 5. Default
  return { icon: <MapPin {...iconProps} />, label: 'Place', type: 'place' };
};